import React, { useMemo, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { Input } from "../../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../ui/select";

// Full list of today's still-unassigned deliveries ("Tiffins Left"), with a
// hover-to-locate on the map and a one-click "move into this route" select
// — an alternative to polygon-drawing/Auto-Assign for placing one stop at a
// time exactly where the manager wants it.
export function UnassignedList({ deliveries, tempRoutes, onHover, onAssign }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return deliveries;
    return deliveries.filter((d) => (d.customer_name || "").toLowerCase().includes(q) || (d.address || "").toLowerCase().includes(q));
  }, [deliveries, query]);

  return (
    <div className="rounded-md border border-border bg-surface p-4" data-testid="unassigned-list-panel">
      <p className="mb-1 font-heading text-sm font-semibold text-text-main">Tiffins Left ({deliveries.length})</p>
      <p className="mb-2 text-xs text-text-muted">Hover a row to find it on the map. Pick a route to move it in directly.</p>
      {deliveries.length > 0 && (
        <div className="relative mb-2">
          <Search size={13} strokeWidth={1.5} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or address..."
            className="h-8 pl-7 text-xs" data-testid="unassigned-list-search"
          />
        </div>
      )}
      <div className="max-h-64 space-y-1 overflow-y-auto" data-testid="unassigned-list">
        {deliveries.length === 0 && (
          <p className="py-4 text-center text-xs text-text-muted" data-testid="unassigned-list-empty">
            All caught up — nothing left unassigned.
          </p>
        )}
        {deliveries.length > 0 && filtered.length === 0 && (
          <p className="py-4 text-center text-xs text-text-muted" data-testid="unassigned-list-no-match">No matches.</p>
        )}
        {filtered.map((d) => {
          const hasCoords = Boolean(d.latitude && d.longitude && d.latitude !== "0");
          return (
          <div
            key={d.id}
            className="flex items-center gap-2 rounded-sm border border-transparent px-2 py-1.5 text-xs transition-colors hover:border-primary hover:bg-primary/5"
            onMouseEnter={() => hasCoords && onHover(d.id)}
            onMouseLeave={() => onHover(null)}
            data-testid={`unassigned-row-${d.id}`}
          >
            <MapPin size={13} strokeWidth={1.5} className="flex-shrink-0 text-text-muted" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-text-main">{d.customer_name || `Delivery #${d.id}`}</p>
              <p className="truncate text-[11px] text-text-muted">{d.address || "No address"}</p>
            </div>
            {hasCoords ? (
              <Select value="" onValueChange={(v) => onAssign(d.id, Number(v))} disabled={tempRoutes.length === 0}>
                <SelectTrigger className="h-7 w-32 flex-shrink-0 text-[11px]" data-testid={`unassigned-move-select-${d.id}`}>
                  <SelectValue placeholder="Move to..." />
                </SelectTrigger>
                <SelectContent>
                  {tempRoutes.map((r) => (
                    <SelectItem key={r.tempId} value={String(r.tempId)}>Route {r.tempId} · {r.driverName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span
                className="flex-shrink-0 rounded-full bg-warning-bg px-2 py-1 text-[10px] font-medium text-warning"
                title="This customer has no geocoded address — fix it on the Customer page before it can be routed."
                data-testid={`unassigned-no-coords-${d.id}`}
              >
                No coordinates
              </span>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
